import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { fieldCount } from './ChangeSummary';
import { Findings, SEVERITY_COLOUR } from './Findings';
import { groupFindings, OUTCOME_LABEL, reviewLabel } from './reviews';

import type { LessonReviews } from '@platform/schema/generation';

type Cards = Omit<Parameters<typeof Findings>[0], 'title' | 'findings'>;

/**
 * The lesson's review, newest first — CURR-55. After a build that is its second review:
 * the first review's findings with what the repair made of them, what only the second
 * review raised, and what needs a developer, each apart. Earlier reviews, and the repairs
 * their findings carry, are one click down.
 */
export function ReviewPanel({
  reviews,
  findings,
  cards,
  attention,
  asking,
  onReviewAgain,
}: {
  /** Undefined until read; a lesson no stored review has read has an empty list. */
  reviews: LessonReviews | undefined;
  /** Every finding the workspace holds, the flat list before CURR-55. */
  findings: Parameters<typeof Findings>[0]['findings'];
  cards: Cards;
  /** A blocking finding still stands, as the lesson's scorecard counts them. */
  attention: boolean;
  /** Review again was clicked and has not been answered yet. */
  asking: boolean;
  onReviewAgain: () => void;
}) {
  const [latest, ...earlier] = reviews?.reviews ?? [];
  if (reviews === undefined || latest === undefined)
    return <Findings {...cards} findings={findings} />;

  // A finding no review filed — a publish rule's — has no group of its own.
  const filed = new Set(
    reviews.reviews.flatMap((one) => one.findings.map((finding) => finding.id)),
  );
  const unlisted = findings.filter(
    (finding) => !filed.has(finding.id) && finding.status !== 'SUPERSEDED',
  );
  const groups = groupFindings(latest.findings);

  return (
    <Stack spacing={1} aria-label="Lesson review" component="section">
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
      >
        <Typography variant="subtitle1" component="h2">
          {reviewLabel(latest, reviews.reviews.length)}
        </Typography>
        <Chip
          size="small"
          color={attention ? 'warning' : 'success'}
          label={attention ? 'Needs attention' : 'No blocking findings'}
        />
      </Stack>

      {reviews.outOfDate ? (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={asking || reviews.reviewing !== undefined}
              onClick={onReviewAgain}
            >
              Review again
            </Button>
          }
        >
          Out of date: the lesson was edited after this review, so the preview
          plays a version it has not read.
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary">
          The preview plays the version this review read.
        </Typography>
      )}

      {reviews.reviewing !== undefined && (
        <Alert severity="info">
          Reviewing the saved lesson now. This panel updates when it finishes.
        </Alert>
      )}

      {latest.findings.length === 0 && (
        <Typography variant="body2">This review raised no findings.</Typography>
      )}

      <Findings
        {...cards}
        title="First-review findings, and what the repair made of each"
        findings={groups.judged}
      />
      <Findings
        {...cards}
        title={
          groups.judged.length > 0
            ? 'Newly raised by this review'
            : 'Raised by this review'
        }
        findings={groups.raised}
      />
      <Findings
        {...cards}
        title="Developer findings: an app change, not a rewrite"
        findings={groups.developer}
      />
      <Findings
        {...cards}
        title="Other findings on this lesson"
        findings={unlisted}
      />

      {earlier.length > 0 && (
        <Accordion variant="outlined" disableGutters>
          <AccordionSummary>
            <Typography variant="body2">
              Earlier reviews and repairs ({String(earlier.length)})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {earlier.map((review, index) => (
                <Stack key={review.versionId} spacing={0.5}>
                  <Typography variant="subtitle2" component="h3">
                    {reviewLabel(review, earlier.length - index)}
                  </Typography>
                  {review.findings.length === 0 && (
                    <Typography variant="body2">No findings.</Typography>
                  )}
                  {review.findings.map((finding) => (
                    <Stack
                      key={finding.id}
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}
                      data-testid="earlier-finding"
                    >
                      <Chip
                        size="small"
                        label={finding.severity}
                        color={SEVERITY_COLOUR[finding.severity]}
                      />
                      {finding.outcome !== undefined && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={OUTCOME_LABEL[finding.outcome]}
                        />
                      )}
                      <Typography variant="body2">{finding.finding}</Typography>
                      {finding.changes !== undefined && (
                        <Typography variant="body2" color="text.secondary">
                          Repair changed {fieldCount(finding.changes)}.
                        </Typography>
                      )}
                    </Stack>
                  ))}
                </Stack>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </Stack>
  );
}
